import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Input,
  useTheme,
  Grid,
  Button,
  Autocomplete,
  TextField
} from '@mui/material';
import SearchIcon from '../../../icons/Search';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import { alpha } from '@mui/material/styles';
import { getTone, grey, lightVeil, darkVeil, greyAlpha, toneAlpha } from '../../../theme/tones';

const SessionFormationsFilter = (props) => {
  // eslint-disable-next-line react/prop-types
  const { setFilters, sessionsFormation = [], availableYears = [] } = props;
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';

  // ── Extractions d'options uniques dynamiques des sessions chargées ──

  // Formats uniques
  // eslint-disable-next-line react/prop-types
  const formatOptions = Array.from(
    new Set(
      sessionsFormation
        // eslint-disable-next-line react/prop-types
        .map((s) => s.format)
        .filter((f) => f && f.trim() !== '')
    )
  ).sort();

  // Types d'action uniques
  // eslint-disable-next-line react/prop-types
  const actionTypeOptions = Array.from(
    new Set(
      sessionsFormation
        // eslint-disable-next-line react/prop-types
        .map((s) => s.action_type)
        .filter((t) => t && t.trim() !== '')
    )
  ).sort();

  // Professions uniques (séparées par virgules)
  const professionsSet = new Set();
  // eslint-disable-next-line react/prop-types
  sessionsFormation.forEach((s) => {
    if (s.professions) {
      s.professions.split(',').forEach((p) => {
        const trimmed = p.trim();
        if (trimmed) professionsSet.add(trimmed);
      });
    }
  });
  const professionOptions = Array.from(professionsSet).sort();

  // Années disponibles : fournies par le backend (toutes années confondues) afin
  // de pouvoir sélectionner une période non chargée par défaut. On retombe sur
  // l'extraction depuis les sessions chargées si la liste n'est pas disponible.
  const yearsSet = new Set();
  // eslint-disable-next-line react/prop-types
  availableYears.forEach((y) => {
    if (y) yearsSet.add(String(y));
  });
  if (yearsSet.size === 0) {
    // eslint-disable-next-line react/prop-types
    sessionsFormation.forEach((s) => {
      if (s.start_date) {
        const parts = s.start_date.split('/');
        if (parts.length === 3) {
          const year = parts[2];
          if (year) yearsSet.add(year);
        }
      }
    });
  }
  const yearOptions = Array.from(yearsSet).sort((a, b) => b - a);

  // Statuts fixes (alignés sur les colonnes du Kanban / getSessionKanbanStatus)
  const statusOptions = [
    'À risque',
    'Confirmée',
    'En cours',
    'Terminée',
    'Annulée'
  ];

  // Options de mois fixes (Français)
  const monthsOptions = [
    { label: 'Janvier', value: 1 },
    { label: 'Février', value: 2 },
    { label: 'Mars', value: 3 },
    { label: 'Avril', value: 4 },
    { label: 'Mai', value: 5 },
    { label: 'Juin', value: 6 },
    { label: 'Juillet', value: 7 },
    { label: 'Août', value: 8 },
    { label: 'Septembre', value: 9 },
    { label: 'Octobre', value: 10 },
    { label: 'Novembre', value: 11 },
    { label: 'Décembre', value: 12 }
  ];

  // ── États locaux pour les filtres ──
  const [searchQuery, setSearchQuery] = useState('');
  const [status, setStatus] = useState(null);
  const [format, setFormat] = useState(null);
  const [actionType, setActionType] = useState(null);
  const [profession, setProfession] = useState(null);
  const [year, setYear] = useState(null);
  const [month, setMonth] = useState(null);
  const [startDateMin, setStartDateMin] = useState('');
  const [startDateMax, setStartDateMax] = useState('');

  const [focused, setFocused] = useState(false);

  // ── Synchronisation réactive avec le composant parent ──
  useEffect(() => {
    setFilters({
      searchQuery,
      status,
      format,
      actionType,
      profession,
      year,
      month: month ? month.value : null,
      startDateMin,
      startDateMax
    });
  }, [
    searchQuery,
    status,
    format,
    actionType,
    profession,
    year,
    month,
    startDateMin,
    startDateMax,
    setFilters
  ]);

  // ── Réinitialisation de tous les filtres ──
  const handleReset = () => {
    setSearchQuery('');
    setStatus(null);
    setFormat(null);
    setActionType(null);
    setProfession(null);
    setYear(null);
    setMonth(null);
    setStartDateMin('');
    setStartDateMax('');
  };

  return (
    <Card
      sx={{
        border: '1px solid',
              borderColor: 'divider',
        borderRadius: '24px',
        overflow: 'hidden',
        boxShadow: (theme) => `0 8px 32px ${alpha(theme.palette.common.black, theme.palette.mode === 'light' ? 0.02 : 0.2)}`,
        backgroundColor: 'background.paper',
        backdropFilter: 'blur(20px)',
        p: 2.5
      }}
    >
      {/* ── BARRE DE RECHERCHE PRINCIPALE & BOUTON REINITIALISER ── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          mb: 2.5
        }}
      >
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            flexGrow: 1,
            width: '100%',
            p: 1.5,
            borderRadius: '18px',
            border: '1px solid',
            borderColor: focused
              ? getTone('blue').base
              : isLight ? greyAlpha(200, 0.8) : lightVeil(0.08),
            backgroundColor: isLight ? grey(50) : greyAlpha(900, 0.25),
            boxShadow: focused ? `0 0 0 3px ${toneAlpha('blue', 0.15)}` : 'none',
            transition: 'all 0.2s ease-in-out',
          }}
        >
          <SearchIcon style={{ color: focused ? getTone('blue').base : grey(400), transition: 'color 0.2s' }} fontSize="small" />
          <Input
            disableUnderline
            fullWidth
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            placeholder="Rechercher par nom de session ou numéro de programme..."
            value={searchQuery}
            sx={{
              fontSize: '0.88rem',
              fontWeight: 500,
              color: 'text.primary',
              '&::placeholder': {
                color: 'text.secondary',
                opacity: 0.8,
              }
            }}
          />
        </Box>

        <Button
          onClick={handleReset}
          startIcon={<RotateLeftIcon />}
          variant="outlined"
          sx={{
            textTransform: 'none',
            fontSize: '0.8rem',
            fontWeight: 700,
            color: 'text.secondary',
            borderColor: 'divider',
            borderRadius: '14px',
            px: 2.5,
            py: 1.5,
            height: '52px',
            flexShrink: 0,
            width: { xs: '100%', sm: 'auto' },
            transition: 'all 0.15s',
            '&:hover': {
              backgroundColor: 'action.hover',
              borderColor: 'error.main',
              color: 'error.main'
            }
          }}
        >
          Réinitialiser
        </Button>
      </Box>

      {/* ── TOUS LES FILTRES SUR LA MEME LIGNE ── */}
      <Grid container spacing={1.5}>
        {/* Combobox Statut */}
        <Grid item xs={12} sm={6} md={1.7}>
          <Autocomplete
            options={statusOptions}
            value={status}
            onChange={(e, val) => setStatus(val)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Statut"
                placeholder="Statut..."
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    backgroundColor: isLight ? 'transparent' : darkVeil(0.1),
                  }
                }}
              />
            )}
          />
        </Grid>

        {/* Combobox Format */}
        <Grid item xs={12} sm={6} md={1.7}>
          <Autocomplete
            options={formatOptions}
            value={format}
            onChange={(e, val) => setFormat(val)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Format"
                placeholder="Format..."
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    backgroundColor: isLight ? 'transparent' : darkVeil(0.1),
                  }
                }}
              />
            )}
          />
        </Grid>

        {/* Combobox Type d'action */}
        <Grid item xs={12} sm={6} md={1.7}>
          <Autocomplete
            options={actionTypeOptions}
            value={actionType}
            onChange={(e, val) => setActionType(val)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Type d'action"
                placeholder="Action..."
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    backgroundColor: isLight ? 'transparent' : darkVeil(0.1),
                  }
                }}
              />
            )}
          />
        </Grid>

        {/* Combobox Professions */}
        <Grid item xs={12} sm={6} md={1.7}>
          <Autocomplete
            options={professionOptions}
            value={profession}
            onChange={(e, val) => setProfession(val)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Professions"
                placeholder="Profession..."
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    backgroundColor: isLight ? 'transparent' : darkVeil(0.1),
                  }
                }}
              />
            )}
          />
        </Grid>

        {/* Combobox Année */}
        <Grid item xs={12} sm={4} md={1.2}>
          <Autocomplete
            options={yearOptions}
            value={year}
            onChange={(e, val) => setYear(val)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Année"
                placeholder="Année..."
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    backgroundColor: isLight ? 'transparent' : darkVeil(0.1),
                  }
                }}
              />
            )}
          />
        </Grid>

        {/* Combobox Mois */}
        <Grid item xs={12} sm={4} md={1.7}>
          <Autocomplete
            options={monthsOptions}
            value={month}
            onChange={(e, val) => setMonth(val)}
            getOptionLabel={(option) => option.label || ''}
            isOptionEqualToValue={(option, val) => option.value === val.value}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Mois"
                placeholder="Mois..."
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    backgroundColor: isLight ? 'transparent' : darkVeil(0.1),
                  }
                }}
              />
            )}
          />
        </Grid>

        {/* Date début ≥ (Saisie manuelle) */}
        <Grid item xs={6} sm={4} md={2}>
          <TextField
            fullWidth
            label="Début ≥"
            placeholder="JJ/MM/AAAA"
            size="small"
            value={startDateMin}
            onChange={(e) => setStartDateMin(e.target.value)}
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                backgroundColor: isLight ? 'transparent' : darkVeil(0.1),
              }
            }}
          />
        </Grid>

        {/* Date début ≤ (Saisie manuelle) */}
        <Grid item xs={6} sm={4} md={2}>
          <TextField
            fullWidth
            label="Début ≤"
            placeholder="JJ/MM/AAAA"
            size="small"
            value={startDateMax}
            onChange={(e) => setStartDateMax(e.target.value)}
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                backgroundColor: isLight ? 'transparent' : darkVeil(0.1),
              }
            }}
          />
        </Grid>
      </Grid>
    </Card>
  );
};

export default SessionFormationsFilter;
